import type { InvocationSuggestion, SuggestionRegion } from "../types.js";

/**
 * Pure shaping of the parsed `breaker-read` suggestions into the rows the
 * right-pane reads table sorts and filters. Kept free of React so the
 * derivation (circuit numbering, amperage joining, pole labelling, the
 * tile back-pointer) is unit-tested directly.
 *
 * The suggestions come from `getInvocation` already parsed — each
 * `breaker-read`'s `proposedValue` is the raw `BreakerReadProposal`
 * (`position`, `pole`, `circuits[].amperage`, `protectionTypes`), with
 * `confidence`, `sourcePhotoIds`, and `regions[]`. A region may carry a
 * `tile` reference (key + tile-space box) — that is the link the
 * trace-to-tile motion follows back to the exact 768 tile.
 */

export type PoleKind = "single" | "double" | "triple" | "tandem" | "quad";

/** The identity fields that carry an independent confidence (KAN-81). */
export type PanelIdentityConfidenceField =
  | "manufacturer"
  | "model"
  | "totalSlots"
  | "numberingScheme";

/** Parsed shape of the panel-identity suggestion (all fields optional). */
export type PanelIdentity = {
  readonly manufacturer?: string;
  readonly series?: string;
  readonly model?: string;
  readonly mainAmperage?: number;
  readonly totalSlots?: number;
  readonly numberingScheme?: string;
  /** Suggestion-level confidence — the fallback for any unscored field. */
  readonly confidence: number;
  /**
   * KAN-81 — optional per-field confidence over the badged identity fields.
   * Absent on pre-KAN-81 invocations; use {@link identityFieldConfidence} to
   * read a field with the suggestion-level fallback applied.
   */
  readonly fieldConfidence?: Partial<Record<PanelIdentityConfidenceField, number>>;
};

/**
 * The confidence for one identity field (KAN-81): the per-field value when the
 * invocation carried one, else the suggestion-level confidence — matching the
 * domain's `panelMetadataFieldConfidence` fallback so the support view and the
 * native client agree on which rows read low.
 */
export function identityFieldConfidence(
  identity: PanelIdentity,
  field: PanelIdentityConfidenceField,
): number {
  return identity.fieldConfidence?.[field] ?? identity.confidence;
}

/** One row of the reads table — a single breaker read, fully shaped. */
export type BreakerRow = {
  readonly suggestionId: string;
  /** User-facing circuit number, derived from position + numbering. */
  readonly circuit: number | null;
  readonly row: number;
  readonly column: number;
  /** `r0·c1`, the model-emitted grid position (positional source of truth). */
  readonly posLabel: string;
  readonly amperages: readonly number[];
  /** `20` or `20/20`, the per-circuit amperages joined. */
  readonly ampLabel: string;
  readonly pole: PoleKind | "unknown";
  /** `Single` / `Tandem` / `2-pole` / … */
  readonly poleLabel: string;
  readonly protectionTypes: readonly string[];
  /**
   * Decoded manufacture date (KAN — Square D Homeline first), when the read
   * carried a date-code stamp the server could decode. `null` otherwise.
   */
  readonly manufactureDate: ManufactureDateView | null;
  readonly confidence: number;
  readonly sourcePhotoId: string | null;
  /**
   * The breaker's bounding box on its source photo's read image, in the same
   * normalized `[0..1]` read-image space as `read.columnBoxes` — so the center
   * pane can highlight exactly where the model read this breaker. `null` when
   * the model emitted no box. (`region.box` is read-image space on the union
   * path: the model reads the rectified read image and its boxes flow through
   * untransformed.)
   */
  readonly region: SuggestionRegion | null;
  /** The `<photoId>::c<col>::t<tile>` key the read came from, when tiled. */
  readonly tileKey: string | null;
  /** The column index parsed out of the tile key, for the Src cell + sort. */
  readonly tileColumn: number | null;
  /** `c0` / `c1`, or `—` when the read has no tile provenance. */
  readonly srcLabel: string;
  /** True when the read can be traced back to a tile (has a tile key). */
  readonly traceable: boolean;
};

function asRecord(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null
    ? (v as Record<string, unknown>)
    : null;
}

/** The one parsed `panel-metadata` suggestion, shaped, or `null`. */
export function panelIdentityOf(
  suggestions: readonly InvocationSuggestion[],
): PanelIdentity | null {
  const match = suggestions.find((s) => {
    const v = asRecord(s.proposedValue);
    return v !== null && v["kind"] === "panel-metadata";
  });
  if (match === undefined) return null;
  const v = asRecord(match.proposedValue);
  if (v === null) return null;
  const scheme = asRecord(v["numberingScheme"]);
  const rawFc = asRecord(v["fieldConfidence"]);
  const fieldConfidence: Partial<Record<PanelIdentityConfidenceField, number>> = {};
  if (rawFc !== null) {
    for (const field of [
      "manufacturer",
      "model",
      "totalSlots",
      "numberingScheme",
    ] as const) {
      const c = rawFc[field];
      if (typeof c === "number") fieldConfidence[field] = c;
    }
  }
  return {
    ...(Object.keys(fieldConfidence).length > 0 ? { fieldConfidence } : {}),
    ...(typeof v["manufacturer"] === "string"
      ? { manufacturer: v["manufacturer"] }
      : {}),
    ...(typeof v["series"] === "string" ? { series: v["series"] } : {}),
    ...(typeof v["model"] === "string" ? { model: v["model"] } : {}),
    ...(typeof v["mainAmperage"] === "number"
      ? { mainAmperage: v["mainAmperage"] }
      : {}),
    ...(typeof v["totalSlots"] === "number"
      ? { totalSlots: v["totalSlots"] }
      : {}),
    ...(scheme !== null && typeof scheme["kind"] === "string"
      ? { numberingScheme: scheme["kind"] }
      : {}),
    confidence: match.confidence,
  };
}

const POLE_LABELS: Record<PoleKind, string> = {
  single: "Single",
  double: "2-pole",
  triple: "3-pole",
  tandem: "Tandem",
  quad: "Quad",
};

function poleOf(v: Record<string, unknown>): {
  kind: PoleKind | "unknown";
  label: string;
} {
  const pole = asRecord(v["pole"]);
  const kind = pole?.["kind"];
  if (
    kind === "single" ||
    kind === "double" ||
    kind === "triple" ||
    kind === "tandem" ||
    kind === "quad"
  ) {
    return { kind, label: POLE_LABELS[kind] };
  }
  return { kind: "unknown", label: "—" };
}

function amperagesOf(v: Record<string, unknown>): number[] {
  const circuits = v["circuits"];
  if (!Array.isArray(circuits)) return [];
  return circuits
    .map((c) => asRecord(c)?.["amperage"])
    .filter((a): a is number => typeof a === "number");
}

/** Extract the column index from a `…::c<col>::t<tile>` tile key. */
export function tileColumnFromKey(tileKey: string | null): number | null {
  if (tileKey === null) return null;
  const m = /::c(\d+)::/.exec(tileKey);
  return m ? Number.parseInt(m[1]!, 10) : null;
}

/**
 * Derive the user-facing circuit number from the model-emitted grid
 * position and the panel's numbering scheme. Mirrors the domain's
 * `userFacingNumber` for the non-custom schemes; falls back to the
 * common `odd-left-even-right` two-column layout when the scheme or
 * slot count isn't known (the panel-identity pass didn't recover it).
 * Returns `null` for a negative/garbage position.
 */
export function circuitNumber(
  row: number,
  column: number,
  identity: PanelIdentity | null,
): number | null {
  if (!Number.isInteger(row) || !Number.isInteger(column)) return null;
  if (row < 0 || column < 0) return null;
  const columns = 2;
  const rows =
    identity?.totalSlots !== undefined
      ? Math.ceil(identity.totalSlots / columns)
      : null;
  switch (identity?.numberingScheme) {
    case "sequential-down-then-over":
      return rows !== null ? column * rows + row + 1 : null;
    case "sequential-left-then-down":
    case "odd-left-even-right":
    default:
      return row * columns + column + 1;
  }
}

/**
 * Shape every `breaker-read` suggestion into a table row. Non-breaker
 * suggestions (panel metadata) are skipped. The order returned is the
 * suggestion order (reading order); the table applies its own sort.
 */
/**
 * The decoded manufacture date as the reads table shows it: a terse label
 * (an ordinal bare-week reads "2024, 21st week"; a weekday-pinned week
 * ISO-resolves to "Dec 9, 2009"), the verbatim stamp it came from, the model's
 * reading confidence, and whether the year was cohort-resolved from legacy
 * alternates.
 */
export type ManufactureDateView = {
  readonly label: string;
  /**
   * The verbatim date-code stamp, for a `scan` reading. `null` for a `manual`
   * reading (a user-asserted date has no stamp) — the UI shows no stamp line.
   */
  readonly raw: string | null;
  /**
   * Reading confidence in `[0, 1]`. A `manual` reading is certain by
   * construction, so it is `1` (no low-confidence badge).
   */
  readonly confidence: number;
  /**
   * True only when a `scan` decode resolved a genuine ambiguity. A `manual`
   * reading is never ambiguous.
   */
  readonly ambiguous: boolean;
};

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * English ordinal suffix: `1st`/`2nd`/`3rd`/`21st`/`50th`/`53rd`, with the
 * 11–13 → `th` exception. Mirrors the domain `ordinal` so TS display stays in
 * parity (kept local rather than importing the domain package, matching this
 * module's deliberately self-contained handling of loose cassette data).
 */
function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/**
 * Resolve an ISO-8601 week date `(year, week, weekday)` (Mon=1 … Sun=7, `year`
 * the week-based year) to a UTC `Date`. Mirrors the domain `isoWeekDateToCalendar`.
 */
function isoWeekDateToCalendar(year: number, week: number, weekday: number): Date {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4IsoDay = jan4.getUTCDay() === 0 ? 7 : jan4.getUTCDay();
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - (jan4IsoDay - 1));
  const result = new Date(week1Monday);
  result.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7 + (weekday - 1));
  return result;
}

function formatCalendar(date: Date): string {
  return `${MONTH_ABBR[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
}

/** Format a decoded `manufactureDate.date` object per its precision arm. */
function formatDecodedDate(date: Record<string, unknown>): string {
  const year = typeof date["year"] === "number" ? date["year"] : null;
  if (year === null) return "?";
  const month = typeof date["month"] === "number" ? date["month"] : null;
  switch (date["precision"]) {
    case "year-month":
      return month !== null ? `${MONTH_ABBR[month - 1]} ${year}` : String(year);
    case "year-week": {
      const week = typeof date["week"] === "number" ? date["week"] : null;
      if (week === null) return String(year);
      // A weekday pins the exact day → ISO-resolve to a calendar date; a bare
      // week straddles a month boundary, so show it as an ordinal week.
      const weekday = typeof date["weekday"] === "number" ? date["weekday"] : null;
      return weekday !== null
        ? formatCalendar(isoWeekDateToCalendar(year, week, weekday))
        : `${year}, ${ordinal(week)} week`;
    }
    case "full-date": {
      const day = typeof date["day"] === "number" ? date["day"] : null;
      return month !== null && day !== null
        ? `${MONTH_ABBR[month - 1]} ${day}, ${year}`
        : String(year);
    }
    default:
      return String(year);
  }
}

/**
 * Pull the manufacture date off a breaker-read proposal, if present. Reads
 * provenance off the `scan` arm (stamp, confidence, ambiguity); a `manual`
 * reading is certain by construction — no stamp, no badges.
 */
function manufactureDateOf(v: Record<string, unknown>): ManufactureDateView | null {
  const md = asRecord(v["manufactureDate"]);
  if (md === null) return null;
  const date = asRecord(md["date"]);
  if (date === null) return null;
  const label = formatDecodedDate(date);
  if (md["source"] === "manual") {
    return { label, raw: null, confidence: 1, ambiguous: false };
  }
  // `scan` (also the default for any legacy wire that predates the `source`
  // discriminant): provenance comes off the stamp.
  const raw = typeof md["rawCode"] === "string" ? md["rawCode"] : null;
  if (raw === null) return null;
  return {
    label,
    raw,
    confidence: typeof md["confidence"] === "number" ? md["confidence"] : 0,
    ambiguous: md["ambiguous"] === true,
  };
}

export function breakerRows(
  suggestions: readonly InvocationSuggestion[],
  identity: PanelIdentity | null,
): BreakerRow[] {
  const rows: BreakerRow[] = [];
  for (const s of suggestions) {
    const v = asRecord(s.proposedValue);
    if (v === null || v["kind"] !== "breaker-read") continue;

    const pos = asRecord(v["position"]);
    const row = typeof pos?.["row"] === "number" ? pos["row"] : 0;
    const column = typeof pos?.["column"] === "number" ? pos["column"] : 0;
    const amperages = amperagesOf(v);
    const pole = poleOf(v);
    const protectionTypes = Array.isArray(v["protectionTypes"])
      ? v["protectionTypes"].filter((p): p is string => typeof p === "string")
      : [];

    // The tile back-pointer: first region carrying tile provenance.
    const tiled = (s.regions ?? []).find((r) => r.tile !== undefined);
    const tileKey = tiled?.tile?.tileKey ?? null;
    const tileColumn = tileColumnFromKey(tileKey);
    const sourcePhotoId = s.sourcePhotoIds[0] ?? tiled?.photoId ?? null;
    // The breaker's own box, for the center-pane highlight. Prefer the region
    // on the source photo (the one the center pane shows); fall back to first.
    const region =
      (s.regions ?? []).find((r) => r.photoId === sourcePhotoId) ??
      (s.regions ?? [])[0] ??
      null;

    rows.push({
      suggestionId: s.id,
      circuit: circuitNumber(row, column, identity),
      row,
      column,
      posLabel: `r${row}·c${column}`,
      amperages,
      ampLabel: amperages.length > 0 ? amperages.join("/") : "—",
      pole: pole.kind,
      poleLabel: pole.label,
      protectionTypes,
      manufactureDate: manufactureDateOf(v),
      confidence: s.confidence,
      sourcePhotoId,
      region,
      tileKey,
      tileColumn,
      srcLabel: tileColumn !== null ? `c${tileColumn}` : "—",
      traceable: tileKey !== null,
    });
  }
  return rows;
}

export type SortKey = "circuit" | "pos" | "amp" | "pole" | "conf" | "src";
export type SortDir = 1 | -1;
export type Sort = { readonly key: SortKey; readonly dir: SortDir };

const POLE_ORDER: Record<PoleKind | "unknown", number> = {
  single: 0,
  tandem: 1,
  double: 2,
  triple: 3,
  quad: 4,
  unknown: 5,
};

function sortValue(r: BreakerRow, key: SortKey): number {
  switch (key) {
    case "circuit":
      return r.circuit ?? Number.MAX_SAFE_INTEGER;
    case "pos":
      return r.row * 100 + r.column;
    case "amp":
      return r.amperages[0] ?? 0;
    case "pole":
      return POLE_ORDER[r.pole];
    case "conf":
      return r.confidence;
    case "src":
      return r.tileColumn ?? Number.MAX_SAFE_INTEGER;
  }
}

export function sortRows(
  rows: readonly BreakerRow[],
  sort: Sort,
): BreakerRow[] {
  return [...rows].sort((a, b) => {
    const delta = sortValue(a, sort.key) - sortValue(b, sort.key);
    // Stable secondary on circuit so equal keys keep a deterministic order.
    if (delta !== 0) return delta * sort.dir;
    return ((a.circuit ?? 0) - (b.circuit ?? 0)) * sort.dir;
  });
}

/** The reads-table filter chips, in display order. */
export type FilterKey =
  | "all"
  | "15A"
  | "20A"
  | "30A"
  | "tandem"
  | "2-pole"
  | "low-conf";

export const FILTERS: readonly { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "15A", label: "15A" },
  { key: "20A", label: "20A" },
  { key: "30A", label: "30A" },
  { key: "tandem", label: "Tandem" },
  { key: "2-pole", label: "2-pole" },
  { key: "low-conf", label: "Low conf" },
];

export function filterRows(
  rows: readonly BreakerRow[],
  filter: FilterKey,
): BreakerRow[] {
  switch (filter) {
    case "all":
      return [...rows];
    case "15A":
      return rows.filter((r) => r.amperages.includes(15));
    case "20A":
      return rows.filter((r) => r.amperages.includes(20));
    case "30A":
      return rows.filter((r) => r.amperages.includes(30));
    case "tandem":
      return rows.filter((r) => r.pole === "tandem");
    case "2-pole":
      return rows.filter((r) => r.pole === "double");
    case "low-conf":
      // Any read the model wasn't fully sure of — the triage shortlist.
      return rows.filter((r) => r.confidence < 1);
  }
}

/** Conf-bar fill colour token: perfect green, ≥0.7 amber, else red. */
export function confidenceTone(
  confidence: number,
): "success" | "warning" | "danger" {
  if (confidence >= 1) return "success";
  if (confidence >= 0.7) return "warning";
  return "danger";
}
